(() => {
  if (window.__REEVYS_PRODUCT_INTELLIGENCE__) return;
  window.__REEVYS_PRODUCT_INTELLIGENCE__ = true;

  const ROOT_SELECTOR = '[data-testid="product-information-details"]';
  const SOURCE_SELECTOR = '.goods-size-table, .product-details-size-fit, .goods-wash, .product-details .details-list';
  const SIZE_OPTION_NAMES = new Set(['size', 'sizes', 'tamanho', 'tamanhos']);
  const rootStates = new WeakMap();

  let activeRoot = null;
  let activeTrigger = null;
  let currentUnit = 'cm';
  let observerTimer = null;

  const cleanText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const normalize = (value) =>
    cleanText(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const normalizeSize = (value) =>
    cleanText(value)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

  function isSizeOptionName(value) {
    return SIZE_OPTION_NAMES.has(normalize(value));
  }

  function parsePairs(section) {
    if (!section) return [];

    return Array.from(section.querySelectorAll('li'))
      .map((item) => {
        const spans = Array.from(item.querySelectorAll('span'))
          .map((span) => cleanText(span.textContent))
          .filter(Boolean);

        if (spans.length >= 2) {
          return { label: spans[0], value: spans.slice(1).join(' ') };
        }

        const text = cleanText(item.textContent);
        const separatorIndex = text.indexOf(':');

        if (separatorIndex > -1) {
          return {
            label: cleanText(text.slice(0, separatorIndex)),
            value: cleanText(text.slice(separatorIndex + 1)),
          };
        }

        return null;
      })
      .filter((item) => item && item.label && item.value);
  }

  function parseCare(section) {
    if (!section) return [];

    return Array.from(section.querySelectorAll('li'))
      .map((item) => cleanText(item.textContent))
      .filter(Boolean)
      .filter((value, index, array) => array.indexOf(value) === index);
  }

  function parseMeasurements(section) {
    if (!section) return null;

    const table = section.querySelector('table');
    if (!table) return null;

    let headers = Array.from(table.querySelectorAll('thead th')).map((cell) => cleanText(cell.textContent));

    if (!headers.length) {
      const headerRow = Array.from(table.querySelectorAll('tr')).find((row) => row.querySelector('th'));
      headers = headerRow
        ? Array.from(headerRow.querySelectorAll('th')).map((cell) => cleanText(cell.textContent))
        : [];
    }

    const readRows = (selector) =>
      Array.from(table.querySelectorAll(selector))
        .map((row) =>
          Array.from(row.querySelectorAll('th, td'))
            .map((cell) => cleanText(cell.textContent))
            .filter((value, index, cells) => value || cells.length === 1 || index < cells.length)
        )
        .filter((row) => row.some(Boolean));

    let cm = readRows('tbody tr.goods-size-cm');
    let inch = readRows('tbody tr.goods-size-in');

    if (!cm.length) cm = readRows('tr.goods-size-cm');
    if (!inch.length) inch = readRows('tr.goods-size-in');

    if (!headers.length || (!cm.length && !inch.length)) return null;

    return { headers, cm, inch };
  }

  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function createInfoSection(title, pairs, careItems) {
    const section = createElement('section', 'reevys-pi-section');
    section.appendChild(createElement('h4', 'reevys-pi-kicker', title));

    if (pairs && pairs.length) {
      pairs.forEach(({ label, value }) => {
        const row = createElement('div', 'reevys-pi-row');
        row.appendChild(createElement('span', 'reevys-pi-label', label));
        row.appendChild(createElement('span', 'reevys-pi-value', value));
        section.appendChild(row);
      });
    }

    if (careItems && careItems.length) {
      careItems.forEach((item) => {
        section.appendChild(createElement('p', 'reevys-pi-care-item', item));
      });
    }

    return section;
  }

  function renderStructuredDetails(host, fit, details, care) {
    if (!host || (!fit.length && !details.length && !care.length)) return false;

    const content = createElement('div', 'reevys-pi-content');
    content.dataset.reevysPiStructured = 'true';

    if (fit.length) content.appendChild(createInfoSection('Fit', fit));
    if (details.length) content.appendChild(createInfoSection('Details', details));
    if (care.length) content.appendChild(createInfoSection('Care', null, care));

    host.replaceChildren(content);
    return true;
  }

  function findDescriptionContent(root) {
    return Array.from(root.querySelectorAll('.details-content')).find((content) =>
      content.querySelector(SOURCE_SELECTOR)
    );
  }

  function findSourceHost(content) {
    if (!content) return null;

    const recognized = Array.from(
      content.querySelectorAll('.goods-size-table, .product-details-size-fit, .goods-wash, .product-details')
    );

    if (!recognized.length) return content;

    let host = recognized[0].parentElement;

    while (
      host &&
      host !== content &&
      !recognized.every((node) => host.contains(node))
    ) {
      host = host.parentElement;
    }

    return host || content;
  }

  function setProductDetailsLabel(content) {
    const details = content?.closest('details');
    const summary = details?.querySelector(':scope > summary.details__header');
    if (!summary) return;

    const textNode = Array.from(summary.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && cleanText(node.textContent)
    );

    if (textNode && /^details$/i.test(cleanText(textNode.textContent))) {
      textNode.textContent = ' Product details ';
    }
  }

  function parseSource(content) {
    const detailsSection = content.querySelector('.product-details');
    const fitSection = content.querySelector('.product-details-size-fit');
    const careSection = content.querySelector('.goods-wash');
    const measurementSection = content.querySelector('.goods-size-table');

    return {
      details: parsePairs(detailsSection),
      fit: parsePairs(fitSection),
      care: parseCare(careSection),
      measurements: parseMeasurements(measurementSection),
    };
  }

  function findSizeOption(root) {
    const input = Array.from(root.querySelectorAll('input[data-option-name]')).find((element) =>
      isSizeOptionName(element.dataset.optionName)
    );

    if (input) {
      const option = input.closest('.variant-option, fieldset');
      const label = option?.querySelector('legend') || null;
      return { option, label };
    }

    const optionElement = Array.from(root.querySelectorAll('option[data-option-name]')).find((element) =>
      isSizeOptionName(element.dataset.optionName)
    );

    if (optionElement) {
      const option = optionElement.closest('.variant-option');
      const label = option?.querySelector('label') || null;
      return { option, label };
    }

    return null;
  }

  function getSelectedSize(root) {
    const checked = Array.from(root.querySelectorAll('input[data-option-name]:checked')).find((element) =>
      isSizeOptionName(element.dataset.optionName)
    );

    if (checked) return cleanText(checked.value);

    const selectedOption = Array.from(root.querySelectorAll('option[data-option-name]:checked')).find((element) =>
      isSizeOptionName(element.dataset.optionName)
    );

    return selectedOption ? cleanText(selectedOption.value) : '';
  }

  function ensureTrigger(root, state) {
    if (!state?.measurements) return;

    const sizeOption = findSizeOption(root);
    if (!sizeOption?.label) return;

    const existing = sizeOption.label.querySelector('.reevys-pm-trigger');
    if (existing) return;

    sizeOption.label.classList.add('reevys-pm-size-label');

    const trigger = createElement('button', 'reevys-pm-trigger', 'Product measurements');
    trigger.type = 'button';
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', 'reevys-product-measurements');
    trigger.addEventListener('click', () => openDrawer(root, trigger));

    sizeOption.label.appendChild(trigger);
  }

  function scanRoot(root) {
    let state = rootStates.get(root) || {};
    const descriptionContent = findDescriptionContent(root);

    if (descriptionContent && !descriptionContent.querySelector('[data-reevys-pi-structured="true"]')) {
      const parsed = parseSource(descriptionContent);

      if (parsed.measurements) {
        state.measurements = parsed.measurements;
      }

      if (parsed.fit.length || parsed.details.length || parsed.care.length) {
        const host = findSourceHost(descriptionContent);
        if (renderStructuredDetails(host, parsed.fit, parsed.details, parsed.care)) {
          setProductDetailsLabel(descriptionContent);
        }
      }

      rootStates.set(root, state);

      if (activeRoot === root && drawerIsOpen()) {
        renderDrawer();
      }
    }

    ensureTrigger(root, state);
  }

  function scanAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(scanRoot);
  }

  function scheduleScan() {
    window.clearTimeout(observerTimer);
    observerTimer = window.setTimeout(scanAll, 80);
  }

  function ensureDrawer() {
    let drawer = document.getElementById('reevys-product-measurements');
    if (drawer) return drawer;

    const overlay = createElement('div', 'reevys-pm-overlay');
    overlay.dataset.reevysPmOverlay = 'true';
    overlay.addEventListener('click', closeDrawer);

    drawer = createElement('aside', 'reevys-pm-drawer');
    drawer.id = 'reevys-product-measurements';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('aria-labelledby', 'reevys-pm-title');

    const head = createElement('div', 'reevys-pm-head');
    const headingGroup = createElement('div');

    const title = createElement('h2', 'reevys-pm-title', 'Product measurements');
    title.id = 'reevys-pm-title';
    headingGroup.appendChild(title);
    headingGroup.appendChild(
      createElement('p', 'reevys-pm-subtitle', 'Measurements specific to this product.')
    );

    const close = createElement('button', 'reevys-pm-close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close product measurements');
    close.addEventListener('click', closeDrawer);

    head.appendChild(headingGroup);
    head.appendChild(close);

    const controls = createElement('div', 'reevys-pm-controls');
    controls.dataset.reevysPmControls = 'true';

    const body = createElement('div', 'reevys-pm-body');
    body.dataset.reevysPmBody = 'true';

    drawer.appendChild(head);
    drawer.appendChild(controls);
    drawer.appendChild(body);

    document.body.appendChild(overlay);
    document.body.appendChild(drawer);

    return drawer;
  }

  function availableUnits(measurements) {
    const units = [];
    if (measurements?.cm?.length) units.push('cm');
    if (measurements?.inch?.length) units.push('inch');
    return units;
  }

  function renderUnitControls(drawer, measurements) {
    const controls = drawer.querySelector('[data-reevys-pm-controls]');
    controls.replaceChildren();

    const units = availableUnits(measurements);

    if (!units.includes(currentUnit)) {
      currentUnit = units[0] || 'cm';
    }

    units.forEach((unit) => {
      const button = createElement(
        'button',
        `reevys-pm-unit${currentUnit === unit ? ' is-active' : ''}`,
        unit === 'cm' ? 'CM' : 'IN'
      );
      button.type = 'button';
      button.setAttribute('aria-pressed', currentUnit === unit ? 'true' : 'false');
      button.addEventListener('click', () => {
        currentUnit = unit;
        renderDrawer();
      });
      controls.appendChild(button);
    });
  }

  function renderTable(drawer, measurements) {
    const body = drawer.querySelector('[data-reevys-pm-body]');
    body.replaceChildren();

    const rows = currentUnit === 'inch' ? measurements.inch : measurements.cm;
    if (!rows?.length) return;

    const wrap = createElement('div', 'reevys-pm-table-wrap');
    const table = createElement('table', 'reevys-pm-table');

    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');

    measurements.headers.forEach((header) => {
      const th = document.createElement('th');
      th.scope = 'col';
      th.textContent = header;
      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    const selectedSize = normalizeSize(getSelectedSize(activeRoot));

    rows.forEach((cells) => {
      const tr = document.createElement('tr');

      if (selectedSize && normalizeSize(cells[0]) === selectedSize) {
        tr.classList.add('is-selected');
      }

      cells.forEach((cell) => {
        const td = document.createElement('td');
        td.textContent = cell;
        tr.appendChild(td);
      });

      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    wrap.appendChild(table);
    body.appendChild(wrap);
  }

  function renderDrawer() {
    if (!activeRoot) return;

    const state = rootStates.get(activeRoot);
    if (!state?.measurements) return;

    const drawer = ensureDrawer();
    renderUnitControls(drawer, state.measurements);
    renderTable(drawer, state.measurements);
  }

  function drawerIsOpen() {
    return document.getElementById('reevys-product-measurements')?.classList.contains('is-open');
  }

  function openDrawer(root, trigger) {
    const state = rootStates.get(root);
    if (!state?.measurements) return;

    activeRoot = root;
    activeTrigger = trigger;
    currentUnit = state.measurements.cm?.length ? 'cm' : 'inch';

    const drawer = ensureDrawer();
    const overlay = document.querySelector('[data-reevys-pm-overlay="true"]');

    renderDrawer();

    drawer.classList.add('is-open');
    overlay?.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('reevys-pm-lock');

    window.requestAnimationFrame(() => {
      drawer.querySelector('.reevys-pm-close')?.focus();
    });
  }

  function closeDrawer() {
    const drawer = document.getElementById('reevys-product-measurements');
    const overlay = document.querySelector('[data-reevys-pm-overlay="true"]');

    drawer?.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    drawer?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('reevys-pm-lock');

    activeTrigger?.focus();
    activeRoot = null;
    activeTrigger = null;
  }

  document.addEventListener('change', (event) => {
    if (!activeRoot || !activeRoot.contains(event.target)) return;

    if (
      event.target.matches('input[data-option-name], select') &&
      (isSizeOptionName(event.target.dataset.optionName) ||
        Array.from(event.target.selectedOptions || []).some((option) =>
          isSizeOptionName(option.dataset.optionName)
        ))
    ) {
      renderDrawer();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!drawerIsOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    const drawer = document.getElementById('reevys-product-measurements');
    const focusable = Array.from(
      drawer.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled])')
    ).filter((element) => element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const observer = new MutationObserver(scheduleScan);

  function init() {
    scanAll();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
